import api from './api-service';
import Transfer from './transfer';
import _ from 'lodash';
import { ASPERA_DOWNLOAD_ROOT } from '~/config';
import Aspera from './aspera';

const TransferManager = {
	credentials: {},
	transfers: [],
	errors: [],

	setCredentials: (session, token) => {
		TransferManager.credentials = { userId: session._id, token };
	},

	logTransfer: async(files) => {
		try {
			const { data } = await api.client.post('/events', {
				from: TransferManager.credentials.userId,
				files: files.map((file) => file._id),
				type: 'fd'
			});
			return data;
		}
		catch{
			const errors = files.map((file) => {
				return {
					path: file.path,
					code: 'px01',
					desc: 'failed to log event'
				};
			});
			throw errors;
		}
	},

	httpDownload: (transferSpec) => {
		const event = window.document.createEvent('MouseEvents');
		let link = window.document.createElement('a');

		link.href = transferSpec.presignedUrl;
		link.download = transferSpec.presignedUrl?.split('?')[0].split('/').pop();

		event.initEvent('click', true, true);
		link.dispatchEvent(event);
	},

	startDownload: (file) => {
		return new Promise((resolve, reject) => api.client.post('/aspera/downloadSpec', { id: file._id })
			.then((res) => {
				const transferSpec = res.data;
				if(Aspera.getStatus() !== 'RUNNING' || Aspera.isDisabled) {
					TransferManager.httpDownload(transferSpec);
					return resolve({});
				}
				Aspera.startTransfer(transferSpec, { allow_dialogs: false })
					.then((result) => {
						const transfer = new Transfer({
							uuid: result.uuid,
							title: file.name,
							percentage: 0,
							rate: 0,
							status: Aspera.TRANSFER_STATUS.INITIATING,
							add_time: new Date(),
							// A download is a `receive` transfer. Set it now so the
							// file list and download controls can match this
							// just-started transfer before Aspera's first activity
							// event (which carries the real transfer_spec) arrives.
							transfer_spec: { direction: 'receive' },
							// We just make up a number greater than 0, percentage complete will be zero
							bytes_expected: 1,
							received: 0
						});
						TransferManager.transfers.push(transfer);
						resolve(transfer);
					})
					.catch((e) => {
						// We resolve failures so that we can handle failures individually later
						resolve({
							error: {
								path: file.path,
								code: e.code,
								user_message: e.user_message ?? e.message
							}
						});
					});
			})
			.catch((e) => {
				// Wrap $http failure as an object with error field, since that what parseErrors expects.
				reject({
					error: {
						path: file.path,
						code: e.status,
						user_message: e.statusText
					}
				});
			}));
	},
	parseErrors: (results) => {
		return _(results)
			.map('error')
			.compact()
			.map((error) => {
				return {
					path: error.path,
					code: error.code,
					desc: error.user_message
				};
			})
			.value();
	},

	add: async(files) => {
		TransferManager.errors = [];
		try {
			await TransferManager.logTransfer(files);
			const results = files.map(TransferManager.startDownload);
			return Promise.all(results).then((res) => {
				let errors = TransferManager.parseErrors(res);

				if(errors.length) {
					throw new Error(errors);
				}

				return files;
			});
		}
		catch(errors) {
			TransferManager.errors = errors;
		}
	},
	upsert: (transfer) => {
		if(Array.isArray(transfer)) {
			return transfer.forEach(TransferManager.upsert);
		}

		const index = TransferManager.transfers.findIndex((item) => item.id === transfer.id);
		if(index < 0) {
			return TransferManager.transfers.push(transfer);
		}

		TransferManager.transfers[index] = transfer;
	},

	remove: (transfer) => {
		if(Array.isArray(transfer)) {
			return transfer.forEach(TransferManager.remove);
		}

		// Mutate the array in place (like `upsert`) instead of reassigning it.
		// Consumers capture `TransferManager.transfers` by reference at render
		// time and aren't re-rendered by Aspera's activity callback, so a
		// reassignment would leave their click handlers reading the old array
		// (and the removed/failed transfer) until the next render.
		const index = TransferManager.transfers.findIndex((item) => item.id === transfer.id);
		if(index >= 0) {
			TransferManager.transfers.splice(index, 1);
		}
	},

	pluginStatus: () => Aspera.getStatus(),

	eventHandler: (data) => {
		if(!data.transfers.length) {
			return;
		}

		data.transfers.forEach((t) => {
			if(t.transfer_spec && t.transfer_spec.remote_host?.startsWith(ASPERA_DOWNLOAD_ROOT)) {
				const transfer = new Transfer(t);
				// A failed transfer must not linger with its last-known
				// (RUNNING/INITIATING) status: the download controls read the live
				// status, so a stale "running" entry would route a click into
				// pause() — which rejects — instead of a fresh download, recreating
				// the very dead-end this fixes (PLATFORM-4050). Drop it like REMOVED.
				switch(transfer.status) {
					case Aspera.TRANSFER_STATUS.REMOVED:
					case Aspera.TRANSFER_STATUS.FAILED:
						TransferManager.remove(transfer);
						break;
					case Aspera.TRANSFER_STATUS.COMPLETED:
					case Aspera.TRANSFER_STATUS.RUNNING:
					case Aspera.TRANSFER_STATUS.CANCELLED:
						TransferManager.upsert(transfer);
						break;
				}
			}
		});

	}

};
export default TransferManager;
